import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowLeft } from "lucide-react";
import { IconX } from "./premium/icons";

const NAV = [
  { id: "intro",       label: "Intro" },
  { id: "why",         label: "Why direct-to-DEX" },
  { id: "launching",   label: "Launching" },
  { id: "mcap",        label: "Custom market caps" },
  { id: "supply",      label: "Custom supplies" },
  { id: "trading",     label: "Trading" },
  { id: "fees",        label: "Fees" },
  { id: "lp",          label: "LP & positions" },
  { id: "claiming",    label: "Claiming" },
  { id: "burn",        label: "Burn mode" },
  { id: "lp-compound", label: "Compounding fees mode" },
  { id: "future",      label: "Future modes" },
  { id: "anti-vamp",   label: "Anti-vamp tickers" },
  { id: "leaderboard", label: "Leaderboard" },
  { id: "jackpot",     label: "Jackpot" },
  { id: "profiles",    label: "Profiles & social" },
  { id: "groups",      label: "Communities" },
  { id: "faq",         label: "FAQ" },
];

export default function DocsPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState("intro");

  useEffect(() => {
    const sections = NAV.map((n) => document.getElementById(n.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (hit) setActive(hit.target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="pm-docs">
      <button className="pm-docs__back" onClick={() => navigate("/")}>
        <ArrowLeft size={14} /> Back
      </button>

      <header className="pm-docs__header">
        <div className="pm-docs__brand">
          <img src="/images/png/slurpface (1).png" alt="Slurp" />
          <div>
            <div className="pm-docs__brand-title">Slurp Docs</div>
            <div className="pm-docs__brand-sub">direct-to-DEX on Solana</div>
          </div>
        </div>
        <Link to="/deploy-token" className="pm-docs__cta">
          <Plus size={16} /> Launch a token
        </Link>
      </header>

      <nav className="pm-docs__nav">
        {NAV.map((n) => (
          <a
            key={n.id}
            href={`#${n.id}`}
            className={`pm-docs__nav-item ${active === n.id ? "is-active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById(n.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            {n.label}
          </a>
        ))}
      </nav>

      <main className="pm-docs__main">
        <Section id="intro" title="What is Slurp">
          <p className="pm-docs__lead">
            Slurp is the first functioning direct-to-DEX memecoin
            launchpad on Solana. Real DEX liquidity from block zero,
            no bonding curve, no graduation, no migration cliff, and
            a full live product built around every token.
          </p>
          <p>
            A launch, mechanically, is a few transactions. Mint a
            token. Open a pool. Hand the user a button. The
            interesting stuff happens around it. How does a token stay
            discoverable after launch? What options does a creator
            actually have over the shape of their token? What's the
            incentive to hold instead of flip? Where do holders talk
            to each other? Where does the chart live? Where does the
            momentum come from once the launchpad's job is technically
            done?
          </p>
          <p>
            For most launchpads, the relationship with your token
            ends the second it hits a pool. Slurp is the opposite.
            The launch is the entry point to a product that stays
            running for the life of the token: the live chart, the
            holders list, the community, the trades feed, the fee
            modes, the leaderboard, and a site-wide feed of every
            launch, trade, burn, and LP compound across the whole
            platform. All of it activates the moment your token
            exists and keeps running as long as the token does.
          </p>
          <p>
            Tokens launch directly onto a Meteora DAMM v2 pool with
            permanently locked LP. No bonding curve. No graduation.
            From block zero your token is a real DEX asset, tradeable
            on every aggregator, wallet, and bot in the Solana
            ecosystem. The pool exists independently of slurp.gg, the
            metadata is immutable, and nothing about your token's
            life depends on us staying online.
          </p>
          <p>
            The launch is yours to shape:
          </p>
          <div className="pm-docs__bullets">
            <Bullet><b>Starting market cap</b> anywhere from $3K to $15K. You pick the price the first trade lands at.</Bullet>
            <Bullet><b>Total supply</b> from 1M up to 1T. Seven steps, your choice. Most launchpads hardcode one.</Bullet>
            <Bullet><b>Three fee modes</b>. Claim them yourself, auto-burn them into the token for continuous deflation, or auto-compound them back into the LP for growing depth. Chosen at launch, locked on-chain.</Bullet>
          </div>
          <p>
            And the product around the launch keeps running. Every
            token gets a live chart, a holders list reconciling
            against on-chain truth, a real-time trades feed, and a
            token-gated community where holders talk to each other. A
            site-wide live feed broadcasts launches, big trades,
            burns, and LP compounds as they happen so no token lives
            in a vacuum. A hold-time-weighted leaderboard, funded by
            12.5% of LP fees on every slurp token, pays SOL
            weekly to the wallets that actually hold instead of flip.
          </p>
          <p>
            <b>That's the bet. The launch is easy. The product is everything around it.</b>
          </p>
          <div className="pm-docs__stickers-row">
            <span className="pm-docs__sticker pm-docs__sticker--cyan">DIRECT TO DEX</span>
            <span className="pm-docs__sticker pm-docs__sticker--orange">LP LOCKED FOREVER</span>
            <span className="pm-docs__sticker pm-docs__sticker--yellow">NO BONDING CURVE</span>
            <span className="pm-docs__sticker pm-docs__sticker--green">CUSTOMISED LAUNCHES</span>
          </div>
        </Section>

        <Section id="why" title="Why direct-to-DEX">
          <p className="pm-docs__lead">
            Bonding curve launchpads have a hidden incentive: keep your
            token on their curve as long as possible. Every trade on the
            curve goes through their site. Their UI, their fee layer,
            their backend pricing. The longer your token stays trapped
            on the curve, the more trades they earn from. The higher the
            graduation target they set, the more they collect before you
            escape.
          </p>
          <p>
            And when migration finally happens, it brings new costs.
            Migration fees. New pool fees. Sometimes a fee split that
            never applied to your bonding-curve trades. The migration tx
            itself can fail, stall, or get sniped, and the chart usually
            resets. The whole journey is designed to keep value flowing
            into the launchpad, not into the token or its holders.
          </p>
          <p>
            Direct-to-DEX inverts all of that. Your token is on Meteora
            DAMM v2 from block zero. Anyone can trade it, on any client,
            any aggregator, any wallet swap. We don't gate trading. We
            don't tax the slurp UI. There's no "graduation" because
            there's nothing to graduate from.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>
              <b>No hidden fees.</b> One flat 1% pool fee per swap, the
              same fee anyone pays trading the pool with or without
              slurp.gg. Zero platform markup on top.
            </Bullet>
            <Bullet>
              <b>Not locked into our site.</b> Trade your slurp-launched
              token on Jupiter, Phantom, Solflare, Birdeye Swap, any
              aggregator, any bot, any script. The pool earns the same
              regardless. We don't care how you swap because we don't
              tax the route.
            </Bullet>
            <Bullet>
              <b>No migration cliff.</b> No graduation event to wait on,
              no migration fee, no chart reset, no "what happens after
              grad" anxiety. Day one and day one thousand look the same.
            </Bullet>
            <Bullet>
              <b>Real liquidity, not a formula.</b> Every price point on
              the chart is a real swap against real reserves. No
              synthetic curve, no virtual liquidity numbers.
            </Bullet>
            <Bullet>
              <b>Composable from launch.</b> Aggregators, DEX screeners,
              wallets, and bots all see your pool from block zero. Your
              token is plugged into the entire Solana ecosystem from the
              first second it exists.
            </Bullet>
          </div>
          <div className="pm-docs__compare">
            <div className="pm-docs__compare-col pm-docs__compare-col--bad">
              <div className="pm-docs__compare-head">Bonding curve pads</div>
              <ul>
                <li>Trades go through their UI, their fee layer</li>
                <li>Higher graduation target = more trades they earn from before you escape</li>
                <li>Synthetic price until graduation, not a real market</li>
                <li>Wait for an arbitrary MC target to "migrate"</li>
                <li>Migration tx can fail, stall, or get sniped</li>
                <li>Migration fees + new pool fees on top of curve fees</li>
                <li>Chart history often resets when the pool migrates</li>
                <li>Liquidity is locked behind the curve, not in a real pool</li>
                <li>Invisible to aggregators and wallet swaps pre-graduation</li>
              </ul>
            </div>
            <div className="pm-docs__compare-col pm-docs__compare-col--good">
              <div className="pm-docs__compare-head">Slurp direct-to-DEX</div>
              <ul>
                <li><b>One flat 1% pool fee.</b> No hidden markup, no slurp-UI tax, no graduation fee</li>
                <li><b>Trade anywhere.</b> Slurp, Jupiter, Phantom, Solflare, any aggregator, any bot</li>
                <li><b>Real pool from block 0.</b> Real price, real liquidity, real volume numbers</li>
                <li><b>No graduation.</b> Your token is on the DEX the moment the launch tx confirms</li>
                <li><b>Fees flow from the first trade.</b> No waiting to earn</li>
                <li><b>LP locked permanently</b> at launch. One tx, no migration risk</li>
                <li><b>Chart starts at block 0</b> and never resets</li>
                <li><b>Composable from launch.</b> Every aggregator and screener sees it instantly</li>
              </ul>
            </div>
          </div>
          <Callout>
            The bonding curve model only makes sense for the launchpad.
            Direct-to-DEX makes sense for everyone else: the creator
            earning fees from block one, the traders getting real
            execution at real prices, and the holders whose liquidity
            isn't hostage to a graduation process.
          </Callout>
        </Section>

        <Section id="launching" title="Launching a token">
          <p>
            Connect your wallet, fill the form, hit Launch. That's it.
          </p>
          <ol className="pm-docs__steps">
            <li><b>Connect</b> a Solana wallet (Phantom, Backpack, Solflare).</li>
            <li><b>Fill the form.</b> Name, ticker, image, supply, starting market cap, optional dev buy.</li>
            <li><b>Sign twice.</b> Two wallet popups, under a minute total.</li>
            <li><b>You're live.</b> Token has a detail page, live chart, trades feed, holders list, and a sharable link.</li>
          </ol>
          <Callout>
            Name, ticker, image, and supply are <b>immutable</b> after launch.
            Mint authority is revoked at launch time so nobody (including the
            creator) can ever mint more.
          </Callout>
        </Section>

        <Section id="mcap" title="Custom market caps">
          <p className="pm-docs__lead">
            Slurp lets you pick your own starting market cap when you
            launch. Other launchpads force every token through the same
            fixed bonding curve at the same starting price. You have no
            say. Slurp puts the slider in the creator's hands.
          </p>
          <p>
            <b>What it controls:</b> the price the very first trade
            happens at. A higher starting MC means each token is worth
            more from block zero, so a smaller buy moves the chart less.
            A lower starting MC gives more upside on early sniping and
            classic memecoin chart patterns.
          </p>
          <p>
            <b>Range:</b> $3K to $15K, step $1K. Default is $3K, where
            most launches live. The MC is anchored to live SOL/USD at
            launch time so the on-chain SOL deposited matches your
            dollar target within a few percent of price slippage.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>
              <b>$3K to $5K:</b> classic memecoin launch. Maximum room
              to run, lots of early-buy upside.
            </Bullet>
            <Bullet>
              <b>$5K to $9K:</b> middle of the road. Trades a bit more
              like an established token from the start. Less explosive
              but still volatile.
            </Bullet>
            <Bullet>
              <b>$9K to $15K:</b> serious launch. For creators who want
              to signal this isn't a flip-token. Less FOMO mechanics,
              more "I'm here to build" energy.
            </Bullet>
          </div>
          <p>
            The starting MC pairs with your <b>dev buy</b> (optional).
            The dev buy is bundled into the same transaction as the
            pool open, so even your own buy can't be front-run. Pick a
            higher starting MC if you want your dev buy to land at a
            non-trivial price; pick lower if you want maximum personal
            upside on your own bag.
          </p>
          <Callout>
            Starting MC is locked at launch. There's no way to migrate
            the pool to a different price after the fact, so pick
            deliberately. The slider on the launch form shows live
            SOL/USD so what you see is what gets deposited.
          </Callout>
        </Section>

        <Section id="supply" title="Custom supplies">
          <p className="pm-docs__lead">
            Slurp also lets you pick your token's total supply at
            launch. Most launchpads hardcode one supply for every
            token. Slurp gives you the choice.
          </p>
          <p>
            <b>Range:</b> 1M, 10M, 100M, 1B, 10B, 100B, or 1T tokens.
            Pick one of seven steps on the launch form.
          </p>
          <p>
            <b>Why it matters:</b> at the same starting market cap, a
            smaller supply means a higher per-token price and a larger
            supply means a lower per-token price. Same dollar value,
            totally different chart aesthetic. 100M tokens at $5K MC
            works out to $0.00005 per token. 1T tokens at $5K MC works
            out to $0.000000005 per token.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>
              <b>1M to 10M:</b> low supply, high per-token price. Each
              token reads as substantial.
            </Bullet>
            <Bullet>
              <b>100M to 1B:</b> mid-range. Common, familiar, easy
              mental math.
            </Bullet>
            <Bullet>
              <b>10B to 1T:</b> high supply, micro-price per token.
              Cheap entry, lots of room for big-number quantity.
            </Bullet>
          </div>
          <p>
            Decimals are fixed at 6, so each token can be split down
            to 0.000001. Plenty of granularity at any supply.
          </p>
          <Callout>
            Like starting MC, supply is locked at launch. Mint
            authority is revoked the moment the pool opens. Nobody,
            including the creator, can ever mint more.
          </Callout>
        </Section>

        <Section id="trading" title="Trading">
          <p>
            Every token has its own page with a live chart, trades feed,
            holders list, and a buy/sell panel on the right. Swaps go through
            Meteora DAMM v2.
          </p>
          <div className="pm-docs__bullets">
            <Bullet><b>1% flat</b> trading fee on every swap. <b>Slurp adds zero fees on top of that.</b> Trading through slurp is free. Other terminals and bots stack their own markup on top of the pool fee; we don't. You pay the 1% pool fee, nothing more. Same fee any direct DAMM v2 swapper pays, with or without our website.</Bullet>
            <Bullet>Slippage configurable at 0.5%, 1%, or 3%.</Bullet>
            <Bullet>Quick-amount buttons for 0.05, 0.1, 0.5, and 1 SOL.</Bullet>
            <Bullet>Prices come from the pool's spot on every tick, not trade-avg.</Bullet>
          </div>
        </Section>

        <Section id="fees" title="Fees · 1% per trade">
          <p>
            Every swap pays a flat <b>1% pool fee</b>. Meteora takes
            their standard <b>20% protocol cut</b> off the top, on every
            DAMM v2 pool. That's non-negotiable and the same on every
            DEX. The remaining <b>80% LP fee</b> is what slurp's design
            splits, four ways:
          </p>
          <div className="pm-docs__split">
            <div className="pm-docs__split-card pm-docs__split-card--creator">
              <div className="pm-docs__split-pct">37.5%</div>
              <div className="pm-docs__split-who">CREATOR</div>
              <div className="pm-docs__split-sub">
                <b>Default:</b> SOL claimable anytime from the portfolio
                page.{" "}
                <b>Burn mode:</b> auto-swapped into the token and burnt
                forever. Creator gets nothing, holders get continuous
                buy pressure.{" "}
                <b>Compound mode:</b> auto-compounded back into the
                pool's liquidity forever. Creator gets nothing, pool
                gets deeper.{" "}
                Mode picked at launch, irrevocable.
              </div>
            </div>
            <div className="pm-docs__split-card pm-docs__split-card--slurp">
              <div className="pm-docs__split-pct">37.5%</div>
              <div className="pm-docs__split-who">SLURP</div>
              <div className="pm-docs__split-sub">
                Funds the platform. Servers, indexers, RPC, ongoing
                feature work. Claimed by the slurp treasury on a
                schedule. Lands as SOL, no token side, never touches
                pool liquidity.
              </div>
            </div>
            <div className="pm-docs__split-card pm-docs__split-card--diamond">
              <div className="pm-docs__split-pct">12.5%</div>
              <div className="pm-docs__split-who">LEADERBOARD</div>
              <div className="pm-docs__split-sub">
                Weekly Diamond Hands prize pool. Pays out as SOL to the
                top wallets on the
                <Link to="/leaderboard"> leaderboard</Link>, ranked by
                hold time × size. Pure reward for sitting on bags
                instead of flipping. No claim flow. SOL lands in your
                wallet at week-end.
              </div>
            </div>
            <div className="pm-docs__split-card pm-docs__split-card--jackpot">
              <div className="pm-docs__split-pct">12.5%</div>
              <div className="pm-docs__split-who">JACKPOT</div>
              <div className="pm-docs__split-sub">
                Site-wide monthly jackpot. Spend $100 buying any slurp
                tokens in a month, any mix counts. You're entered with
                an equal chance to win. Whales don't get more tickets.
                One random winner per month takes the full pot.
              </div>
            </div>
            <div className="pm-docs__split-card pm-docs__split-card--ui">
              <div className="pm-docs__split-pct">0%</div>
              <div className="pm-docs__split-who">SLURP UI</div>
              <div className="pm-docs__split-sub">
                We charge nothing extra for trading through our website.
                Other terminals and bots stack their own markup on top
                of the pool fee. We don't. You pay the same 1% pool fee
                whether you trade through slurp, Jupiter, Phantom, any
                aggregator, or a direct DAMM v2 swap.
              </div>
            </div>
          </div>
          <Callout>
            Only the SOL side of fees is distributed. Token-side fees stay in
            the pool, so creators, slurp, the leaderboard, and the jackpot
            always get paid in SOL, never in the token.
          </Callout>
        </Section>

        <Section id="lp" title="LP & positions">
          <p>
            At launch, the entire supply is seeded into a single-sided LP
            position on Meteora DAMM v2, then atomically split between Slurp
            and the creator. Both positions are permanently locked. Neither
            side can ever withdraw liquidity.
          </p>
          <div className="pm-docs__bullets">
            <Bullet><b>Locked from block 0.</b> LP can never be pulled.</Bullet>
            <Bullet>Fees accrue in a V3-style accumulator, separate from active reserves, so claiming doesn't move the price.</Bullet>
            <Bullet>Each side owns a position NFT. Whoever holds the NFT can claim that position's fees.</Bullet>
            <Bullet>Claiming never affects the underlying liquidity.</Bullet>
          </div>
        </Section>

        <Section id="claiming" title="Claiming fees">
          <p>
            Creators claim their 37.5% LP-fee share from the{" "}
            <Link to="/portfolio">portfolio page</Link>. Every launched token
            has a "Fees generated" widget with a Claim button. One click, one
            tx, SOL in your wallet.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>Balances refresh on-chain every 20 seconds.</Bullet>
            <Bullet>Claim anytime. No cooldown, no vesting, no minimum.</Bullet>
            <Bullet>Creator pays the tx fee (roughly 0.00003 SOL).</Bullet>
            <Bullet>Slurp's share is claimed server-side by the treasury on a schedule. Nothing you need to do.</Bullet>
          </div>
        </Section>

        <Section id="burn" title="Burn mode">
          <p>
            At launch, every creator picks how to handle their 37.5%
            LP-fee share: <b>Gimme my fees</b> (default, you claim SOL
            like normal) or <b>Let 'em burn baby</b>. Burn-mode tokens
            get a flame badge on every card and detail page.
          </p>
          <p>
            For burn-mode tokens fees are <b>auto bought back and burnt
            forever</b>. The creator can't claim them. At launch, the
            authority over their fee position is permanently handed to a
            public burn wallet that can only swap fees into the token
            and burn them. No human in the loop, no manual button, no
            opt-out.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>The burn worker scans <b>hourly</b>. When a token's pending fees reach <b>0.1 SOL</b> or more, it claims, buys back, and burns in one tx.</Bullet>
            <Bullet>The bought tokens are burned via SPL <code>BurnChecked</code>. Total supply ticks down. Forever.</Bullet>
            <Bullet>The "Total Burnt" sticky on the detail page updates live for everyone. Fully auditable on Solscan.</Bullet>
            <Bullet>The burn wallet (address ends in <code>bUrN</code>) is the authority. Every claim+swap+burn it signs is on-chain proof.</Bullet>
          </div>
          <p>
            <b>Why creators pick this:</b> it aligns them 100% with
            holders. They make money only if the token does. Every trade
            is a tiny buy-back-and-burn. Continuous price support plus
            permanent supply reduction. The flame badge is the public
            pledge.
          </p>
          <p>
            <b>Why this stops fee farming on rubbish launches:</b>{" "}
            without burn mode, a creator can spam dozens of low-effort
            tokens, generate small fees on each, and pocket the SOL
            regardless of whether the token went anywhere. With burn
            mode, the creator's payout IS the token doing well. Fees
            evaporate as supply burns, and there's nothing to farm. It
            filters for creators who actually believe in their launch
            instead of running a fee mill on disposable tickers.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>Slurp's 37.5% LP-fee share works the same regardless of mode (fees are claimed as plain SOL by the treasury).</Bullet>
            <Bullet>Meteora's standard 20% protocol cut is unchanged.</Bullet>
            <Bullet>Burn mode is set at launch and is irrevocable. The creator gives up the position NFT in the same launch transaction. No take-backs.</Bullet>
          </div>
        </Section>

        <Section id="lp-compound" title="Compounding fees mode">
          <p>
            A third option at launch: <b>Send 'em to the LP</b>. The
            creator's 37.5% LP-fee share doesn't get claimed and doesn't
            get burnt. It gets <b>auto-compounded back into the pool's
            liquidity, forever</b>. Pool reserves grow on every claim
            cycle. The chart gets harder to dump on as the token ages.
          </p>
          <p>
            Same atomic hand-off pattern as burn mode: at launch the
            creator-side position NFT goes to a public, dedicated
            wallet (address ends in <code>groW</code>) that can ONLY
            claim those fees, swap them into the pool, and addLiquidity
            back into the same locked position. No human in the loop,
            no opt-out.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>
              The compound worker scans <b>hourly</b>. When a token's
              pending fees reach <b>0.1 SOL</b> or more, one atomic
              transaction claims the SOL, swaps it for tokens (minus the
              tiny slippage paid to the pool), then adds both sides back
              as liquidity. Pool reserves tick up. Tracked live on the
              detail page as <b>Fees added to LP</b>.
            </Bullet>
            <Bullet>
              The swap shows on DexScreener as a buy from{" "}
              <code>dvqdjX5…groW</code>; the addLiquidity shows as an
              Add event from the same wallet. Fully on-chain auditable.
            </Bullet>
            <Bullet>
              Creator earns <b>zero SOL</b> from this token. Their
              return is entirely "the chart got better, my pool got
              deeper". Aligns the creator with long-term holders, not
              short-term flippers.
            </Bullet>
            <Bullet>
              The split is computed dynamically from the position's
              current value ratio. Near launch most of each fee batch
              swaps to tokens, very little goes in as SOL. As the
              token's price walks up the SOL share grows naturally.
            </Bullet>
            <Bullet>
              Slurp's 37.5% LP-fee share and Meteora's 20% protocol cut
              are unchanged. The leaderboard (12.5%) and jackpot (12.5%)
              are still funded as normal.
            </Bullet>
            <Bullet>
              Mode is picked at launch and irrevocable. The creator
              hands over the position NFT in the same launch transaction.
              No take-backs.
            </Bullet>
          </div>
          <p>
            <b>Why creators pick this:</b> it's the strongest possible
            "I'm not here to fee-farm" signal. Burn shrinks supply;
            compounding deepens the pool. Both end with the creator
            making zero SOL, but a compound-mode token gets more
            tradeable the longer it lives, while a burn-mode token gets
            scarcer. Pick whichever vibe your project needs.
          </p>
          <p>
            <b>Where it shines:</b> compound mode tends to feel best on
            launches with a starting market cap around <b>$3k to $8k</b>.
            That's the sweet spot where each compound cycle visibly
            deepens a still-thin pool. Higher starting MCs work too,
            and on a token that finds real volume, the LP can grow very
            thick very quickly as fees pour back in. That's a feature,
            not a bug, but it's worth knowing what you're signing up
            for. Pick the starting MC that matches the chart you want.
          </p>
        </Section>

        <Section id="future" title="Future modes">
          <p className="pm-docs__lead">
            Future modes will be put to the community vote. We want
            Slurp users to feel like they're part of a family and have
            input into decision making.
          </p>
          <p>
            Keep an eye on our socials for public votes.{" "}
            <b>SLURRRRRP It.</b>
          </p>
        </Section>

        <Section id="anti-vamp" title="Anti-vamp tickers">
          <p>
            When you pick a ticker, Slurp checks against everything
            launched on the platform in the last hour. If someone else
            already used that ticker recently, you get bounced with a
            message like <i>"$XXX just launched 12 min ago, pick
            something different and let theirs cook for an hour."</i>
          </p>
          <p>
            <b>Why this exists:</b> serial bot launchers and ticker
            farmers were spamming dozens of clones every time a ticker
            trended (news drops, X posts, meme moments). It splits
            liquidity across copycats, confuses traders, and steals
            momentum from whoever launched first. The 1-hour cooldown
            gives the original a fair window to actually find a market
            before the vampires move in.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>Check is case-insensitive. $cat, $CAT, $Cat all collide.</Bullet>
            <Bullet>1-hour window starts at the original launch's confirmed tx. After 60 min the ticker frees up.</Bullet>
            <Bullet>Different ticker, same name? That's fine. Only the ticker is rate-limited.</Bullet>
            <Bullet>$SLURP and $SIURP (the lookalike) are reserved for the platform forever.</Bullet>
          </div>
          <p>
            This is a community-driven feature. Multiple creators asked
            for it after watching their launches get drowned out by
            instant clones. It's not a moderation policy, it's a
            speed-bump against farming.
          </p>
        </Section>

        <Section id="leaderboard" title="Leaderboard">
          <p className="pm-docs__lead">
            A 7-day rolling <b>Diamond Hands</b> board that rewards
            holders, not flippers. The longer you sit on a position, the
            higher you climb. The top of the board takes real SOL at the
            end of every week.
          </p>
          <p>
            <b>12.5% of LP fees</b> on every slurp token goes into a
            weekly Diamond Hands prize pool. At the end of each 7-day
            cycle the pool is paid directly to the top wallets on the
            board. No claim flow, no points. Just SOL sent to your
            connected wallet.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>
              Score is <code>totalVolume^0.3 × avgHoldHours^0.7</code>.
              Patience is weighted roughly twice as hard as size. A $1k
              bag held 24h beats a $100k flip in a minute.
            </Bullet>
            <Bullet>
              Positions held under <b>60 seconds</b> or worth under{" "}
              <b>$20</b> count as zero. No sniper-speed or sybil-dust
              farming.
            </Bullet>
            <Bullet>
              FIFO matching. Sells close out your oldest buys first.
              Partial sells scale your score down proportionally. You
              can't game it by leaving 1 token behind.
            </Bullet>
            <Bullet>
              7-day rolling window. Old wins roll off so every week is a
              fresh sprint. Recomputes hourly from on-chain data, fully
              auditable on Solscan.
            </Bullet>
          </div>
        </Section>

        <Section id="jackpot" title="Jackpot">
          <p className="pm-docs__lead">
            One winner. One month. A site-wide pot funded by every trade
            on every slurp token.
          </p>
          <p>
            <b>12.5% of LP fees</b> on every slurp token flows into a
            monthly jackpot pool. At the end of each month, one random
            wallet is drawn from everyone who qualifies, and the full
            pot lands in their wallet.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>
              <b>How to enter.</b> Spend at least <b>$100 buying slurp
              tokens</b> across the month. Any mix of tokens counts.
              The entry is the buy itself. Hit $100 cumulative and
              you're in the draw.
            </Bullet>
            <Bullet>
              <b>Equal chance for everyone past the threshold.</b>
              Whales don't get more tickets, $100 in buys gives you
              the same odds as $10,000 in buys. One ticket per
              qualifying wallet.
            </Bullet>
            <Bullet>
              <b>One winner per month.</b> Random draw from every
              qualifying wallet at month-end.
            </Bullet>
            <Bullet>
              <b>Funded pool-wide.</b> Every trade on every slurp token
              contributes, regardless of which UI executed the swap
              (slurp, Jupiter, Phantom, any aggregator).
            </Bullet>
            <Bullet>
              <b>Pot accumulates all month.</b> Drawn at month-end.
              Paid out as SOL straight to the winning wallet.
            </Bullet>
          </div>
        </Section>

        <Section id="profiles" title="Profiles & social">
          <p className="pm-docs__lead">
            A launchpad isn't just a tool, it's a place. Slurp has built-in
            wallet profiles so you actually know who's behind the tokens
            you're trading, who's shipping consistently, and who you trust
            to follow into the next launch.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>
              <b>Profiles.</b> Claim a username, drop a polaroid avatar,
              add a bio and your socials (X, Telegram, GitHub, website).
              Every wallet on Slurp gets a public page that lists their
              launches, holdings, and trade history. Anonymous wallets
              still work, they just show as a short address.
            </Bullet>
            <Bullet>
              <b>Verify your X.</b> Pasting your handle into your profile
              shows it as a link. Hitting <i>Verify with X</i> next to the
              field takes you through a one-tap X login and stamps a
              cyan check on your X icon, so anyone looking at your
              profile knows the handle and the wallet are the same
              person. Verification is free; no posting, following, or
              public activity is touched.
            </Bullet>
            <Bullet>
              <b>Follow creators you trust.</b> Tap follow on any profile
              and you'll get pinged in your notifications the moment they
              ship a new token. No more refreshing X to catch a stealth
              launch.
            </Bullet>
            <Bullet>
              <b>Notifications.</b> A bell in the top bar tracks new
              followers, new launches from people you follow, and other
              activity tied to your wallet. Persistent until you read
              them.
            </Bullet>
            <Bullet>
              <b>Public history.</b> Anyone can view anyone else's
              profile, their launches, what they hold, what they've
              traded. Reputation is real, on-chain, and yours to build.
            </Bullet>
          </div>
          <p>
            We're not stopping there. This whole layer is where Slurp gets
            interesting longer-term: more social signals, better discovery,
            tighter loops between creators and the people who actually
            care about their tokens. Memecoins live and die on community,
            so we're building for it.
          </p>
        </Section>

        <Section id="groups" title="Communities">
          <p className="pm-docs__lead">
            Every token on Slurp gets its own Community, a private,
            token-gated forum where the people actually holding the bag
            can talk, post, run polls, and build culture together. No
            random Telegram link, no Discord overhead, no impersonators.
            Wallet-verified entry, signed posts, on-chain receipts.
          </p>
          <div className="pm-docs__bullets">
            <Bullet>
              <b>Why this matters.</b> Memecoins live and die on community,
              and right now that community is scattered across Telegram
              groups (closed, no SEO, no proof of who's in there), Twitter
              replies, and the occasional Discord. Slurp puts the chat
              where the trade happens, with one rule: hold the token, get
              in the room.
            </Bullet>
            <Bullet>
              <b>Token-gated, no exceptions.</b> The creator picks a
              minimum balance to join, set in tokens or as a percentage
              of supply. The backend verifies your on-chain holdings the
              moment you tap Join. No holdings, no entry. No bots. No
              outside noise. Members stay until they leave or get kicked,
              even if they later sell, so the room stays loyal to the
              people who showed up early.
            </Bullet>
            <Bullet>
              <b>Whale tier.</b> If the join threshold is set to 1% of
              supply or higher, the room flips from "$TICKER Holders" to
              "$TICKER Whales" with a teal accent across the index and
              header. Same mechanics, different vibe. The room signals
              that getting in took serious size.
            </Bullet>
            <Bullet>
              <b>Threads, replies, and polls.</b> Members start threads,
              reply, attach images, and vote in polls. Replies nest so a
              long debate stays threaded instead of becoming a flat scroll.
              Every post and comment is signed by the author's wallet, so
              nobody can fake being someone else.
            </Bullet>
            <Bullet>
              <b>Likes, dislikes, and edits.</b> Each thread and comment
              has a thumbs-up / thumbs-down. One reaction per wallet,
              tap again to clear, switch sides freely. Authors can edit
              their own posts (an "edited" tag appears so the room knows)
              or delete them outright. Deleted images are wiped from
              storage in the same flow.
            </Bullet>
            <Bullet>
              <b>Holder rank badges.</b> Every author chip shows their
              standing in the token: TOP 10, TOP 100, or TOP 1% based
              on live on-chain holdings. Real signal in a sea of anons.
              You can tell at a glance if a hot take is from a tourist
              or a top-10 bag holder.
            </Bullet>
            <Bullet>
              <b>Reports and auto-moderation.</b> Hit Report on anything
              that breaks the rules. Three distinct reports auto-hide
              the post pending admin review, so spam doesn't sit on the
              wall while admins sleep. Reports are rate-limited (5 per
              hour per wallet) and require an account at least an hour
              old, which kills the burner-flood vector before it starts.
            </Bullet>
            <Bullet>
              <b>Reply notifications.</b> When someone replies to your
              thread or comment, the bell in the top bar lights up and
              clicking the notification jumps you straight to the reply.
              Don't want them? One tap on the mute toggle in the bell
              menu and they stop, just for that wallet on that device.
            </Bullet>
            <Bullet>
              <b>Creator and admin tools.</b> The token creator is the
              group's first admin and can promote any member to admin
              (or demote them later). Admins pin threads, hide
              rule-breakers, ban accounts, edit the banner, description,
              and the separate Group Rules editor, and create polls to
              poll holders. Banned wallets can't silently rejoin under
              the same address. The "kick non-holders" tool re-checks
              every member's balance against the threshold and removes
              anyone below it in one click. Useful for threshold-gated
              rooms when the chart pumps and people start selling out.
            </Bullet>
            <Bullet>
              <b>Discoverable.</b> The Communities page lists every
              active room sorted by recent activity, member count, or
              newest. Search by ticker or name. New holders find their
              tribe in seconds, and creators with active rooms get free
              traffic from anyone browsing the index.
            </Bullet>
            <Bullet>
              <b>What this means for creators.</b> A token with an active
              Community is a token with sticky users. Every chat, poll,
              and meme inside the room is one more reason for holders to
              come back to Slurp tomorrow instead of forgetting about
              your bag. Run AMAs, drop hints, ship updates, share wins,
              build the lore.
            </Bullet>
          </div>
          <p>
            Open Communities in the top nav to browse what's live, or hit
            your token page and tap Create Community to spin one up. Free
            to create, free to join, locked to your token's holders.
          </p>
        </Section>

        <Section id="faq" title="FAQ">
          <Faq q="Do I need KYC?">
            No. Connect wallet, launch, done. Slurp never sees your identity.
          </Faq>
          <Faq q="Can the creator pull LP?">
            No. LP is locked forever via Meteora DAMM v2's lock flag at pool
            creation. Only trading fees are claimable. The underlying
            liquidity is untouchable.
          </Faq>
          <Faq q="How are the fees split?">
            Every swap pays a 1% pool fee. Meteora takes their standard
            20% protocol cut at the DEX level, same as on any DAMM v2
            pool, with or without slurp. The remaining 80% LP fee splits
            four ways: <b>37.5% to creators, 37.5% to slurp, 12.5% to
            the weekly leaderboard, 12.5% to the monthly jackpot</b>.
            All paid in SOL.
          </Faq>
          <Faq q="Is the supply fixed?">
            Yes. Mint authority is revoked at launch. Nobody, including the
            creator, can mint more of your token after launch.
          </Faq>
          <Faq q="Can I change my token's image or ticker after launch?">
            No. Metadata is set at launch time and its update authority is
            revoked. Pick carefully.
          </Faq>
          <Faq q="What's the dev buy?">
            An optional SOL amount that the creator swaps into their own token
            the moment the pool opens. The buy is bundled into the launch flow
            so bots can't front-run between pool creation and your first
            purchase.
          </Faq>
          <Faq q="How much does a launch cost?">
            Around <b>$5</b>, and <b>Slurp keeps zero of it</b>.
            That covers Solana account rent + Meteora's pool creation fee +
            tx priority fees. It all goes to Solana validators and Meteora,
            never to us. Plus whatever you pick for the optional dev buy.
            <br /><br />
            Why charge anything if we don't take a cut? Two reasons:
            <br /><br />
            <b>1. Stops spam.</b> A free launchpad gets thousands of "test"
            tokens and copy-paste rugs an hour. The $5 floor isn't enough
            to deter a real creator, but it filters out the bots that would
            otherwise drown out actual launches.
            <br /><br />
            <b>2. Stops fee farmers.</b> A free launch + creator fee
            mechanic = perverse incentive to spam launches just to harvest
            random small fees. The cost makes that math not work.
          </Faq>
          <Faq q="Which chain?">
            Solana. Mainnet only. No EVM, no testnets.
          </Faq>
          <Faq q="How do I claim my creator fees?">
            Hit <Link to="/portfolio">Portfolio</Link>, find your token,
            mash Claim. SOL lands in your wallet in one tx. Burn and
            compound tokens skip this step entirely. The worker is the
            one with the keys, you just enjoy the chart.
          </Faq>
          <Faq q="Burn vs compound. Which one's cooler?">
            Both end with the creator earning <b>zero SOL</b>, both are
            forever, both are flexes. <b>Burn</b> torches supply with
            every fee tick, the float gets thinner, scarcity goes brrr.
            <b> Compound</b> shovels every fee back into the pool so the
            chart gets harder to dump on as it ages. Burn = supply
            squeeze. Compound = liquidity glow up. Pick the vibe.
          </Faq>
          <Faq q="When do burn and compound actually fire?">
            Workers scan <b>hourly</b>. They only pull the trigger once
            a token has racked up at least <b>0.1 SOL</b> in pending
            fees, so quiet tokens don't bleed gas on nothing. When they
            do fire, the whole claim plus swap plus burn (or
            addLiquidity) happens in one atomic Solana tx. Fully
            on-chain, fully auditable.
          </Faq>
          <Faq q="Can I switch my fee mode after launch?">
            Nope. Mode gets baked into the position NFT at launch and
            for burn and compound, the NFT gets shipped to a public,
            single-purpose wallet in the same transaction. Nobody has
            those keys, including us. Choose your fighter on the launch
            screen.
          </Faq>
          <Faq q="What's the PAID sticker on tokens?">
            That means the creator has paid for the enhanced profile on{" "}
            <a href="https://dexscreener.com" target="_blank" rel="noreferrer">DexScreener</a>:
            verified badge, header image, proper socials, the works. It
            does nothing for trading mechanics. It's a "this team is
            actually showing up" signal.
          </Faq>
          <Faq q="Why does the 24h change show a dash?">
            Because nothing's traded yet (or nothing in the past 24h).
            We refuse to fake a "0.00%" when there's no real comparison
            price. A dash means no data, not flat.
          </Faq>
          <Faq q="How do I get paid from the leaderboard?">
            You don't. <b>It pays you.</b> 12.5% of LP fees on every
            slurp token piles into a weekly Diamond Hands prize pool. At
            the end of the 7-day cycle, SOL goes <i>straight</i> to the
            top wallets on the board. No claim button, no points, no
            quest. Just SOL appearing in your wallet because you held.
            Scoring formula lives in <Link to="/docs#leaderboard">Leaderboard</Link>.
          </Faq>
          <Faq q="What's a profile? Do I need one?">
            Free, optional, recommended. Grab a username, slap on a
            polaroid avatar, drop your bio plus socials. Now anyone
            visiting your wallet sees who's behind the launches instead
            of <code>3xK2..bnA9</code>. Skip it and your wallet still
            works fine, you'll just look anonymous. Profile unlocks
            follows, notifications, and a public launches/holdings page.
          </Faq>
          <Faq q="How do follows and notifications work?">
            Tap follow on any profile and the bell in the top bar lights
            up the second they ship a new token. Notifications live
            server-side, so reading them on your phone clears them on
            your laptop too. Solves the "refreshing X to catch a stealth
            launch" problem.
          </Faq>
          <Faq q="Why did Slurp block my ticker?">
            Anti-vamp collision. We block duplicate tickers for the
            first <b>1 hour</b> after any launch so the original gets a
            clean shot at the market before the clones swarm. The error
            tells you exactly when the cooldown clears. Pick something
            spicier in the meantime. See <Link to="/docs#anti-vamp">Anti-vamp tickers</Link>.
          </Faq>
          <Faq q="Which wallets work?">
            Pretty much everything on Solana. Tap Connect and you'll see:
            Phantom, Solflare, Backpack, Glow, Coinbase, Trust, Bitget,
            Ledger, Trezor, SafePal, xDefi, Salmon, Nightly, Krystal,
            NuFi, MathWallet, Coin98, TokenPocket, Clover, Bitpie, Spot,
            Neko, Onto, Huobi, and anything else that speaks the Solana
            Wallet Standard. Modern wallets like Phantom, Solflare,
            Backpack and Glow auto-register the moment you visit; the
            rest are one tap away in the connect modal. Works on desktop
            and mobile (in-app browser or deep-link).
          </Faq>
          <Faq q="What are Communities?">
            Every token gets its own token-gated forum at{" "}
            <code>/groups/MINT</code>. Holders chat, post threads, drop
            polls, like and react. The catch: you have to <b>actually
            hold the token</b> to post. Read access is open to the world,
            but the post button is gated by your wallet's on-chain
            balance. No bots, no shillers from outside, just people
            with skin in the game.
          </Faq>
          <Faq q="How much do I need to hold to post in a community?">
            The threshold is set by whoever creates the community
            (default: any non-zero balance). Some communities require
            ≥1% of supply for "whale" mode with a different title and
            blue accent. Members modal shows everyone who currently
            qualifies, sorted by balance.
          </Faq>
          <Faq q="Why are only the top 20 holders shown?">
            Performance + clarity. Anything past rank 20 is dust by
            definition (less than 0.5% of supply for most tokens). We
            re-fetch the top 20 directly from chain when the tab opens,
            so balances are accurate to the second, better than a long
            list of stale rows. Pool-related addresses (the Meteora
            pool authority, position NFTs, etc.) are filtered out so the
            list only shows real wallet holders.
          </Faq>
          <Faq q="Why does my token's holders count look slightly off?">
            Two reasons. (1) The on-chain reconcile only fires when the
            holders tab is opened, then caches for 60 seconds, so a
            sale that just happened might take up to a minute to show.
            (2) We hide protocol addresses (pool authority, position
            NFTs) from the public list, so the real "wallet holders"
            number is one or two less than what some block explorers
            show.
          </Faq>
          <Faq q="What's the image size limit?">
            1 MB max per token image. PNG, JPEG, GIF, or WebP.
            Recommended at least 400×400 so it stays sharp on the
            polaroid and chart card.
          </Faq>
          <Faq q="How long does a launch actually take?">
            About 8 to 15 seconds end to end. Two wallet popups: one for
            the mint creation, one for the pool and fee split. Each
            confirms on chain before the next, and during heavy Solana
            congestion the whole thing can stretch to 20s. We use
            dynamic priority fees so launches stay competitive even
            when the network is hot.
          </Faq>
          <Faq q="Why does Phantom show a warning when I launch?">
            Phantom warns on every fresh dApp domain by default for the
            first few days, then auto-clears. It also flags the launch
            tx itself because mint creation + authority revocation
            matches a "high-risk" pattern Phantom uses (rugs do this
            too, but so do all legit launchpads, including Pump.fun
            and Bonk.fun). Click Approve, your launch is real and safe.
            We've submitted slurp.gg to Phantom's review team to clear
            the warning permanently.
          </Faq>
          <Faq q="What happens to test or spam tokens?">
            We don't manually moderate. Token cards sort by activity
            (Bumps default), so dead and never-traded tokens drift to
            the back of the grid naturally.
          </Faq>
          <Faq q="My token didn't appear right after launch, what gives?">
            On-chain finished, but our backend missed the receipt POST.
            Refresh the page in 30 seconds. The indexer's catch-up
            sweep picks it up automatically. If still missing after a
            minute, check the browser console for a "[launch] receipt
            POST exhausted retries" warning and ping support with the
            tx signature.
          </Faq>
          <Faq q="Is my token safe?">
            LP is locked forever. Mint authority is revoked. Metadata
            is immutable. That's about as rug-proof as a Solana pool
            gets. But crypto is volatile and any token can go to zero.
            DYOR.
          </Faq>
          <Faq q="I have more questions.">
            Hit our support bot on Telegram:{" "}
            <a href="https://t.me/Slurp_Support_bot" target="_blank" rel="noreferrer">@Slurp_Support_bot</a>.
            For everything else, slide into the{" "}
            <a href="https://t.me/slurpdotgg" target="_blank" rel="noreferrer">main Telegram</a>{" "}
            or hit us on{" "}
            <a href="https://x.com/slurpdotgg" target="_blank" rel="noreferrer">X</a>.
            We're around.
          </Faq>
        </Section>

        <div className="pm-docs__footer">
          <img src="/images/png/slurpface (1).png" alt="" />
          <div>
            <div className="pm-docs__footer-brand">slurp.gg</div>
            <div className="pm-docs__footer-tag">direct-to-DEX on Solana</div>
          </div>
          <div className="pm-docs__contact">
            <span className="pm-docs__contact-label">Contact us:</span>
            <a
              className="pm-docs__contact-link"
              href="https://x.com/slurpdotgg"
              target="_blank"
              rel="noreferrer"
              aria-label="Slurp on X"
            >
              <IconX />
              <span>@slurpdotgg</span>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="pm-docs__section">
      <h2 className="pm-docs__title">{title}</h2>
      <div className="pm-docs__content">{children}</div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <div className="pm-docs__bullet">
      <span className="pm-docs__bullet-dot" />
      <div>{children}</div>
    </div>
  );
}

function Callout({ children }) {
  return <div className="pm-docs__callout">{children}</div>;
}

function Faq({ q, children }) {
  return (
    <details className="pm-docs__faq">
      <summary>{q}</summary>
      <div className="pm-docs__faq-body">{children}</div>
    </details>
  );
}
